import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ListadoCostos } from '@/components/Costos/ListadoCostos';
import { ModalCosto } from '@/components/Costos/ModalCosto';
import { MODO_EDICION_COSTOS } from '@/components/Costos/config';
import type { BasesCalculo, Costo, CostoFormData, ParteItem, TipoMoneda } from '@/types/costo';
import { ETIQUETA_CAJA } from '../constants/presupuestos';
import { formatearMonto } from '../utils/formatters';
import { IndicadorGuardado } from './IndicadorGuardado';
import type { DestinoPagoOpcion, EstadoGuardado } from '../types';

interface CajaItemsProps {
  parte: ParteItem;
  items: Costo[];
  onCrearItem: (data: CostoFormData) => Promise<unknown>;
  onActualizarItem: (itemId: string, data: Partial<CostoFormData>) => Promise<unknown>;
  onEliminarItem: (itemId: string) => void;
  /** Subtotales por moneda de esta caja. */
  subtotales?: Record<TipoMoneda, number>;
  estadoGuardado?: EstadoGuardado;
  onReintentar?: () => void;
  isLoading?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Guarda el orden de impresión de la caja; sin él no se ofrece reordenar. */
  onReordenar?: (ids: string[]) => Promise<unknown>;
  /** Encabezado alternativo (por ejemplo, dentro de una escritura). */
  titulo?: string;
  /** Acciones extra en el encabezado de la caja. */
  acciones?: React.ReactNode;
  isSplitView?: boolean;
  splitContainer?: HTMLElement | null;
  /** Permite ítems calculados sobre la base imponible (presupuestos proforma). */
  permitirPorcentaje?: boolean;
  /** Bases del presupuesto sobre las que se calculan los ítems porcentuales. */
  bases?: BasesCalculo;
  /** Cotización del dólar del presupuesto, para ítems en otra moneda que su base. */
  cotizacionDolar?: number | null;
  /** Destinos de pago para elegir en cada ítem. */
  destinosPago?: DestinoPagoOpcion[];
  /** Abre la edición del cálculo de un ítem. */
  onEditarCalculo?: (item: Costo) => void;
}

/**
 * Caja de ítems de una parte.
 *
 * La categoría de los ítems queda determinada por la caja: acá no hay ningún
 * selector de adquiriente / transferente, ni al crear ni al editar.
 */
export const CajaItems: React.FC<CajaItemsProps> = ({
  parte,
  items,
  onCrearItem,
  onActualizarItem,
  onEliminarItem,
  subtotales,
  estadoGuardado = 'idle',
  onReintentar,
  isLoading = false,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  onReordenar,
  titulo,
  acciones,
  isSplitView = false,
  splitContainer = null,
  permitirPorcentaje = false,
  bases,
  cotizacionDolar = null,
  destinosPago,
  onEditarCalculo,
}) => {
  const [modalAbierto, setModalAbierto] = useState(false);

  const totales: Record<TipoMoneda, number> = subtotales ?? {
    ARS: items.filter((i) => i.moneda === 'ARS').reduce((total, i) => total + i.monto, 0),
    USD: items.filter((i) => i.moneda === 'USD').reduce((total, i) => total + i.monto, 0),
  };

  const monedasConItems = (['ARS', 'USD'] as TipoMoneda[]).filter(
    (moneda) => totales[moneda] !== 0 || items.some((item) => item.moneda === moneda)
  );

  return (
    <Card data-testid={`caja-${parte}`}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{titulo ?? ETIQUETA_CAJA[parte]}</CardTitle>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <IndicadorGuardado estado={estadoGuardado} onReintentar={onReintentar} />
          {acciones}
          {canCreate && (
            <Button size="sm" onClick={() => setModalAbierto(true)} disabled={isLoading}>
              <Plus className="mr-1 h-4 w-4" />
              Agregar ítem
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <ListadoCostos
          costos={items}
          onActualizarCosto={onActualizarItem}
          onEliminarCosto={onEliminarItem}
          isLoading={isLoading}
          canEdit={canEdit}
          canDelete={canDelete}
          modoEdicion={MODO_EDICION_COSTOS}
          onReordenar={onReordenar}
          mensajeVacio={`Sin ítems de ${parte} todavía`}
          onEditarCalculo={permitirPorcentaje ? onEditarCalculo : undefined}
          bases={bases}
          cotizacionDolar={cotizacionDolar}
          destinosPago={destinosPago}
        />

        <div className="flex flex-col items-end gap-1 border-t pt-3 text-sm">
          {monedasConItems.length === 0 ? (
            <span className="font-semibold">
              Subtotal: {formatearMonto(0, 'ARS')}
            </span>
          ) : (
            monedasConItems.map((moneda) => (
              <span key={moneda} className="font-semibold">
                Subtotal {moneda}: {formatearMonto(totales[moneda], moneda)}
              </span>
            ))
          )}
        </div>
      </CardContent>

      <ModalCosto
        isOpen={modalAbierto}
        onClose={() => setModalAbierto(false)}
        onConfirm={async (data) => {
          await onCrearItem(data);
        }}
        parte={parte}
        titulo={`Agregar ítem — ${ETIQUETA_CAJA[parte]}`}
        isLoading={isLoading}
        isSplitView={isSplitView}
        splitContainer={splitContainer}
        permitirPorcentaje={permitirPorcentaje}
        bases={bases}
        cotizacionDolar={cotizacionDolar}
        destinosPago={destinosPago}
      />
    </Card>
  );
};
